// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { Box, Button, Heading } from 'grommet';
import { FormClose, Subtract } from 'grommet-icons';
import styled from 'styled-components';


import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { APIContext, Confirmation, withConfirmation } from 'speed-js-react';

import { FormCheckmark } from 'grommet-icons/icons';
import { openPanel } from 'esm-web-pro/redux/side-panel';

import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import ButtonContainer from 'esm-web-pro/components/layout/sidePanel/ButtonContainer';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import Plus from 'esm-web-pro/components/layout/sidePanel/icons/Plus';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import Attributes from 'esm-web-pro/components/layout/sidePanel/Attributes';
import CableIcon from 'esm-web-pro/components/layout/sidePanel/icons/CableIcon';
import CableModelColorSchemeForm from 'esm-web-pro/containers/pages/network/forms/CableModelColorSchemeForm';
import Editor from 'esm-web-pro/components/pages/projectManagement/dashboard/Editor';
// eslint-disable-next-line max-len
import FiberColorSchemeAttribute from 'esm-web-pro/containers/pages/administration/infrastructure/FiberColorSchemeAttribute';

const SubtractIconButton = () => (
    <Box
        direction="row"
        justify="center"
        gap="small"
    >
        <Button
            icon={<Subtract />}
            plain={false}
            style={{ padding: '3px', borderWidth: '1px' }}
        />
    </Box>
);


const messages = defineMessages({
    addColorScheme: {
        id: 'add-color-scheme',
        defaultMessage: 'Add color scheme',
    },
    editColorScheme: {
        id: 'edit-color-scheme',
        defaultMessage: 'Edit color scheme',
    },
    removeColorScheme: {
        id: 'remove-color-scheme',
        defaultMessage: 'Remove color scheme',
    },
    confirmDeleteColorScheme: {
        id: 'confirm-delete-color-scheme',
        defaultMessage: 'Are you sure you want to delete this color scheme?',
    },
    confirmCancelAddingColorScheme: {
        id: 'confirm-cancel-adding-color-scheme',
        defaultMessage: 'Are you sure you want to cancel adding color scheme?',
    },
    somethingWentWrong: {
        id: 'error-fallback-message',
        defaultMessage: 'Something went wrong. Please try again.',
    },
});

const CABLE_MODEL_SIDE_PANEL_KEY = 'CABLE_MODEL_SIDE_PANEL_KEY';

type Props = {
    object: Object,
    reload: Function,
    panelId: string,
    onClose: () => void,
    confirm: (confirmation: Confirmation) => void,
};

const CableModelSidePanel = (props: Props) => {
    const {
        object: cableModel,
        confirm,
        panelId,
        reload,
        onClose,
    } = props;

    const { apiClient } = React.useContext(APIContext);

    const [addingColorScheme, setAddingColorScheme] = React.useState(false);
    const [currentColorScheme, setCurrentColorScheme] = React.useState(null);

    const dispatch = useDispatch();
    const intl = useIntl();


    const confirmErrorMessage = React.useCallback((message) => confirm({
        message,
        actions: {
            ok: {
                label: <FormattedMessage id="cancel-btn" defaultMessage="Cancel" />,
                callback: () => {},
                type: 'negative',
            },
        },
    }), [confirm]);

    const invokeConfirmDialog = React.useCallback((message, confirmCallback) => confirm({
        message,
        actions: {
            cancel: {
                label: <FormattedMessage id="cancel-btn" defaultMessage="Cancel" />,
                callback: () => {},
                type: 'negative',
            },
            confirm: {
                label: <FormattedMessage id="confirm-btn" defaultMessage="Confirm" />,
                callback: confirmCallback,
                type: 'positive',
            },
        },
    }), [confirm]);


    const addCableModelColorScheme = (newColorScheme) => {
        const fiberCount = parseInt(cableModel.fibers_count.split('_').pop(), 10);

        // From this data only the legacy_nomenclature_item_id and color_scheme are taken into account
        // all other data is overwritten by the serializer in backend, since it is copied from the nomenclature item
        const requestData = {
            legacy_nomenclature_item_id: cableModel.id,
            color_scheme: newColorScheme,
            name: cableModel.translations.bg.name,
            fiber_count: fiberCount,
            tube_count: cableModel.tube_count,
            eit_infrastructure_layer_id: cableModel.eit_infrastructure_layer_id,
        };


        apiClient.resources.cableModel.create(requestData).then((response) => {
            setAddingColorScheme(false);
            reload();
        }).catch((error) => {
            const errMsg = error.data[0] || intl.formatMessage(messages.somethingWentWrong);
            confirmErrorMessage(errMsg, () => {});
        });
    };

    const editCableModelColorScheme = (updatedColorScheme) => {
        const requestData = { color_scheme: updatedColorScheme };

        apiClient.resources.cableModel.partialUpdate(cableModel.color_scheme_id, requestData).then((response) => {
            setAddingColorScheme(false);
            reload();
        }).catch((error) => {
            const errMsg = error.data[0] || intl.formatMessage(messages.somethingWentWrong);
            confirmErrorMessage(errMsg, () => {});
        });
    };


    const handleAddColorScheme = React.useCallback((newColorScheme) => {
        addCableModelColorScheme(newColorScheme);
    }, []);

    const handleCancelAddColorScheme = React.useCallback(() => {
        setAddingColorScheme(false);
    }, []);

    const handleEditColorScheme = React.useCallback(() => {
        setCurrentColorScheme(cableModel.color_scheme);
        setAddingColorScheme(true);
    }, []);


    const handleDeleteColorScheme = () => {
        apiClient.resources.cableModel.delete(cableModel.color_scheme_id).then((response) => {
            reload();
        }).catch((error) => {
            const errMsg = error.data[0] || intl.formatMessage(messages.somethingWentWrong);
            confirmErrorMessage(errMsg, () => {});
        });
    };

    const cableModelAttributes = [
        {
            label: <FormattedMessage id="nomenclatureitem-code" defaultMessage="Code" />,
            value: cableModel.code,
        },
        {
            label: <FormattedMessage id="nomenclature-is-active" defaultMessage="Is Active" />,
            value: cableModel.is_active ? <FormCheckmark /> : <FormClose />,
        },

        {
            label: <FormattedMessage id="cable-attributes-fiber-count" defaultMessage="Fiber count" />,
            value: cableModel.fibers_count,
        },
        {
            label: <FormattedMessage id="cable-attributes-standard" defaultMessage="Cable standard" />,
            value: cableModel.cable_standard,
        },
        {
            label: <FormattedMessage id="fiber-model" defaultMessage="Fiber model" />,
            value: cableModel.fiber_model,
        },
        {
            label: <FormattedMessage id="tube-count" defaultMessage="Tube count" />,
            value: cableModel.tube_count,
        },
        {
            label: <FormattedMessage id="diameter" defaultMessage="Diameter" />,
            value: cableModel.diameter,
        },
        {
            label: <FormattedMessage id="bearing" defaultMessage="Bearing" />,
            value: cableModel.bearing ? <FormCheckmark /> : <FormClose />,
        },
        {
            label: <FormattedMessage id="cable-is-armored" defaultMessage="Armed" />,
            value: cableModel.armed ? <FormCheckmark /> : <FormClose />,
        },
        {
            label: <FormattedMessage id="cable-manufacturer" defaultMessage="Manufacturer" />,
            value: cableModel.manufacturer,
        },
        {
            label: <FormattedMessage id="infrastructure-code" defaultMessage="Infrastructure code" />,
            value: cableModel.eit_infrastructure_code,
        },
        {
            label: <FormattedMessage id="infrastructure-layer-id" defaultMessage="EIT Infrastructure layer id" />,
            value: cableModel.eit_infrastructure_layer_id,
        },
        {
            label: <FormattedMessage id="export-default" defaultMessage="Export default" />,
            value: cableModel.eit_export_default,
        },

    ];


    const cableModelActions = [
        {
            key: `${cableModel.id}-edit`,
            icon: <EditIcon size="small" />,
            callback: () => dispatch(openPanel(
                CABLE_MODEL_SIDE_PANEL_KEY,
                '',
                null,
                true,
            )),
            tooltip: intl.formatMessage(actionMessages.edit),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        },
        {
            key: `${cableModel.id}-delete`,
            icon: <DeleteIcon size="small" />,
            callback: () => handleDeleteColorScheme(cableModel.color_scheme_id),
            tooltip: intl.formatMessage(actionMessages.delete),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        },
    ];

    // Push the add/remove color scheme action in the middle of the actions array
    if (cableModel.color_scheme) {
        // Add an edit and delete action for the color scheme after the first action
        // Add after the remove color scheme action
        cableModelActions.splice(1, 0, {
            key: `${cableModel.id}-edit-color-scheme`,
            icon: <EditIcon size="small" />,
            callback: () => handleEditColorScheme(),
            tooltip: intl.formatMessage(messages.editColorScheme),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        });

        cableModelActions.splice(2, 0, {
            key: `${cableModel.id}-remove-color-scheme`,
            icon: <SubtractIconButton />,
            callback: () => invokeConfirmDialog(
                intl.formatMessage(messages.confirmDeleteColorScheme),
                handleDeleteColorScheme,
            ),
            tooltip: intl.formatMessage(messages.removeColorScheme),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        });
    } else {
        cableModelActions.splice(1, 0, {
            key: `${cableModel.id}-add-color-scheme`,
            icon: <Plus size="small" />,
            callback: () => setAddingColorScheme(true),
            tooltip: intl.formatMessage(messages.addColorScheme),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        });
    }

    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            continuity
            title={cableModel.translations.bg.name}
            titleLabel={<FormattedMessage id="cable-model-label" defaultMessage="Cable Model" />}
            titleIcon={<CableIcon />}
            actions={
                !addingColorScheme
                    && <ButtonContainer handleClick={onClose} />
            }
        >
            <>
                {!addingColorScheme ? (
                    <Box height="100%" justify="between">
                        <Box flex>
                            <HorizontalLine color="grey" />
                            <UpperContainer>
                                <Attributes attributes={cableModelAttributes} />
                                <Box direction="row">
                                    <Editor
                                        id={CABLE_MODEL_SIDE_PANEL_KEY}
                                        object={cableModel}
                                        resourceName="cableModelNomenclature"
                                        idAccessor="id"
                                        editorTitle={(
                                            <FormattedMessage
                                                id="edit-cable-model"
                                                defaultMessage="Edit Cable Model"
                                            />
                                        )}
                                        onSave={reload}
                                    />
                                    <MainActions main actions={cableModelActions} />
                                </Box>

                                {cableModel.color_scheme && (
                                    <Box>
                                        {cableModel.color_scheme.map((tube, tubeIndex) => (
                                            <Box key={`${cableModel.id}-${tube.id}`} flex={false}>
                                                <StyledHorizontalLine />

                                                <Box direction="row" align="center" justify="between">
                                                    <StyledHeading margin="none">
                                                        {`Tube ${tube.id}`}
                                                    </StyledHeading>
                                                </Box>

                                                <StyledHorizontalLine />

                                                {tube.fibers.map((fiber, fiberIndex) => (
                                                    <FiberColorSchemeAttribute
                                                        key={fiber.id}
                                                        tubeIndex={tubeIndex}
                                                        fiber={fiber}
                                                        fiberIndex={fiberIndex}
                                                    />
                                                ))}


                                            </Box>
                                        ))}
                                    </Box>
                                )}

                            </UpperContainer>
                            <HorizontalLine color="grey" />
                            <BottomContainer>
                                <Box height={{ min: '100%' }} />
                            </BottomContainer>
                        </Box>
                    </Box>
                ) : (

                    <CableModelColorSchemeForm
                        key={cableModel.id}
                        values={cableModel}
                        initialValues={currentColorScheme || []}
                        onSubmit={handleAddColorScheme}
                        isEdit={!!currentColorScheme}
                        onEdit={editCableModelColorScheme}
                        onClose={() => invokeConfirmDialog(
                            intl.formatMessage(messages.confirmCancelAddingColorScheme),
                            handleCancelAddColorScheme,
                        )}
                    />
                )}
            </>
        </SidePane>
    );
};

CableModelSidePanel.defaultProps = {
    panelId: CABLE_MODEL_SIDE_PANEL_KEY,
};

const StyledHeading = styled(Heading)`
    line-height: unset;
    white-space: nowrap;
    font-size: 16px;
  `;

const StyledHorizontalLine = styled.hr`
    color: grey;
    background-color: grey;
    width: 100%;
    display: flex;
`;

export default withConfirmation(CableModelSidePanel);
